import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { SwapText } from "./swap-text";

/** How long a wait runs before the screen owns up to it. */
export const SLOW_AFTER_MS = 10_000;

/** What the quiet line says once a wait has run past `SLOW_AFTER_MS`. */
export const SLOW_NOTE = "This is taking longer than usual.";

/**
 * Whether a wait that began at `since` (a `performance.now()` reading) has run past `after`. A new
 * `since` is a new wait, so a retry starts the count again rather than staying slow.
 */
export function useSlow(since: number, after = SLOW_AFTER_MS): boolean {
  // The `since` the screen went slow for, so a changed `since` is not slow until it is.
  const [slowFor, setSlowFor] = useState<number | null>(() =>
    performance.now() - since >= after ? since : null,
  );
  useEffect(() => {
    const remaining = Math.max(0, after - (performance.now() - since));
    const timer = setTimeout(() => setSlowFor(since), remaining);
    return () => clearTimeout(timer);
  }, [since, after]);
  return slowFor === since;
}

/**
 * The product's mark as the thing that waits: its three tiles rise in turn. This is the one
 * whole-screen indicator; `Loader` stays for an action in progress beside its label.
 *
 * Like `Loader`, it keeps the document's rhythm rather than its own, so one that replaces another
 * mid-wait carries on where the last was. `still` rests it, for a screen whose wait is over.
 * Decorative; the title beside it says what is pending.
 */
export function WaitMark({ still = false }: { still?: boolean }) {
  const ref = useRef<SVGSVGElement>(null);
  useLayoutEffect(() => {
    // Not every DOM has the Web Animations API (test environments); there the rise simply starts.
    for (const animation of ref.current?.getAnimations?.({ subtree: true }) ?? []) {
      animation.startTime = 0;
    }
  }, []);
  return (
    <svg
      ref={ref}
      className="wait-mark"
      data-slot="wait-mark"
      data-still={still ? "" : undefined}
      viewBox="0 0 40 40"
      fill="none"
      aria-hidden="true"
    >
      <g strokeWidth="1.8" strokeLinejoin="round">
        <path d="M8.6 20.3 14.2 14.7V26.7L8.6 32.3Z" fill="#1c1b19" stroke="#1c1b19" />
        <path d="M17.2 16.8 22.8 11.2V23.2L17.2 28.8Z" fill="#6c6863" stroke="#6c6863" />
        <path d="M25.8 13.3 31.4 7.7V19.7L25.8 25.3Z" fill="#aaa59d" stroke="#aaa59d" />
      </g>
    </svg>
  );
}

/**
 * The quiet line under a wait's title. It always holds its height, so the title never moves when
 * the line fills in, and it turns into the slow note in place when the wait runs long.
 */
export function WaitDetail({ slow, note = "" }: { slow: boolean; note?: string }) {
  return (
    <p className="wait-detail">
      <SwapText text={slow ? SLOW_NOTE : note} />
    </p>
  );
}

/** What a slow wait offers: only there once it is slow, so a quick one never shows them. */
export function WaitActions({ slow, children }: { slow: boolean; children: ReactNode }) {
  if (!slow) return null;
  return <div className="wait-actions">{children}</div>;
}
